import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { unwrapConnection } from "@utils/utils";
import React, { useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Label, Small } from "@fronterahealth/frontera-ui-components";

import { ReportGoalType, ReportTargetType } from "@api/graphql/types-and-hooks";
import { GoalLineRow } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalLineRow";
import { TargetRow } from "@pages/ClinicalReportDetails/ReportPage/Components/TargetRow";
import { useReportProgrammingData } from "@providers/ReportProgrammingProvider";

interface GoalsSidebarSearchProps {
  reportId: string;
  goalList: ReportGoalType[];
  onSearchComplete?: () => void;
}

interface SearchResultGoal {
  goal: ReportGoalType;
  matched: boolean;
}

interface SearchResultTarget {
  target: ReportTargetType;
  goal: ReportGoalType;
}

export const GoalsSidebarSearch: React.FC<GoalsSidebarSearchProps> = ({ goalList, onSearchComplete }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGoals, setExpandedGoals] = useState(true);
  const [expandedTargets, setExpandedTargets] = useState(true);
  const { dispatchSelected, selected } = useReportProgrammingData();

  const searchLower = searchTerm.toLowerCase();

  const { uniqueGoals, matchingTargets, totalResults } = useMemo(() => {
    if (!searchTerm) return { uniqueGoals: [], matchingTargets: [], totalResults: 0 };

    const goals: SearchResultGoal[] = [];
    const targets: SearchResultTarget[] = [];
    const processedGoalIds = new Set<string>();

    goalList.forEach((goal) => {
      const goalNameMatch = goal.goalName.toLowerCase().includes(searchLower);
      const reportTargets = (unwrapConnection(goal.reporttargetSet) as ReportTargetType[]) || [];

      if (goalNameMatch) {
        goals.push({ goal, matched: true });
        processedGoalIds.add(goal.id);
      }

      reportTargets.forEach((target) => {
        const targetNameMatch = target.targetName.toLowerCase().includes(searchLower);
        if (targetNameMatch) {
          targets.push({ target, goal });
          if (!processedGoalIds.has(goal.id)) {
            processedGoalIds.add(goal.id);
          }
        }
      });
    });

    const unique = Array.from(new Map(goals.map((g) => [g.goal.id, g])).values());

    return {
      uniqueGoals: unique,
      matchingTargets: targets,
      totalResults: unique.length + targets.length,
    };
  }, [searchTerm, goalList, searchLower]);

  return (
    <div className="flex w-[345px] flex-col border-r border-border-medium bg-white">
      <div className="flex items-center justify-between border-b pl-1.5 pr-2 min-h-12">
        <div className="relative flex items-center gap-1 flex-1">
          <div className="flex items-center gap-2 flex-1 border-2 border-green-700 rounded-md pr-3 pl-1 py-1 -m-px">
            <MagnifyingGlassIcon className="h-[18px] w-[18px] text-icon-secondary shrink-0" />
            <input
              type="text"
              placeholder="Search for goals..."
              value={searchTerm}
              autoFocus
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 bg-white outline-hidden text-sm border-0 focus:ring-offset-0 focus:ring-0 p-0 placeholder-text-tertiary"
            />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button className="rounded-sm p-1" onClick={onSearchComplete}>
            <XMarkIcon className="h-4 w-4 text-icon-secondary" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {searchTerm && totalResults === 0 ? (
          <Small className="text-text-tertiary text-center">No matching goals found</Small>
        ) : searchTerm && totalResults > 0 ? (
          <div className="flex flex-col gap-4">
            <Small className="text-text-tertiary">{totalResults} results found</Small>

            {/* Goals Section */}
            {uniqueGoals.length > 0 && (
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => setExpandedGoals(!expandedGoals)}
                  className="flex items-center gap-1 text-text-secondary hover:text-text-primary px-1"
                >
                  <ChevronDownIcon
                    className={twMerge("h-4 w-4 transition-transform", !expandedGoals && "rotate-180")}
                  />
                  <Label size="extra-small" displayType="loud" colorType="secondary">
                    Goals
                  </Label>
                </button>

                {expandedGoals && (
                  <div className="flex flex-col gap-2">
                    {uniqueGoals.map((result) => (
                      <GoalLineRow
                        goal={result.goal}
                        isSelected={selected.mode === "goal" && selected.goalId === result.goal.id}
                        onGoalClick={() => dispatchSelected({ type: "SELECT_GOAL", goalId: result.goal.id })}
                        highlightTerm={searchTerm}
                        key={result.goal.id}
                        className="border p-2 rounded-lg border-limestone-400 min-h-12"
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Targets Section */}
            {matchingTargets.length > 0 && (
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => setExpandedTargets(!expandedTargets)}
                  className="flex items-center gap-1 text-text-secondary hover:text-text-primary px-1"
                >
                  <ChevronDownIcon
                    className={twMerge("h-4 w-4 transition-transform", !expandedTargets && "rotate-180")}
                  />
                  <Label size="extra-small" displayType="loud" colorType="secondary">
                    Targets
                  </Label>
                </button>

                {expandedTargets && (
                  <div className="flex flex-col gap-2">
                    {matchingTargets.map((result) => (
                      <TargetRow
                        key={result.target.id}
                        target={result.target}
                        isSelected={selected.mode === "target" && selected.targetId === result.target.id}
                        onClick={() =>
                          dispatchSelected({
                            type: "SELECT_TARGET",
                            targetId: result.target.id,
                            goalId: result.goal.id,
                          })
                        }
                        highlightTerm={searchTerm}
                        goalName={result.goal.goalName}
                        className="border p-2 rounded-lg border-limestone-400 min-h-12"
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <Small className="text-text-tertiary text-center">
            Start by searching for goals and targets by their titles
          </Small>
        )}
      </div>
    </div>
  );
};
